import { useState, useCallback, useMemo } from "react";
import {
  ApiResponse,
  ApiDataResponse,
  PromotionRuleDto,
  PromotionEligibilityDto,
  GetStudentPromotionDto,
  BulkPromotionRequestDto,
  ApprovePromotionDto,
  RejectPromotionDto,
  SessionChangePromotionDto,
  PromotionPagedData,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";
import useThemeColors from "../useThemeColors";
import useIcons from "../useIcons";
import useToaster from "../useToaster";
import { PromotionStatus, PromotionType } from "../../services/Enums";
import { useStudentsContext } from "../../Contexts/StudentsContext";

export const usePromotionService = () => {
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [eligibilityData, setEligibilityData] = useState<
    PromotionEligibilityDto[]
  >([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [eligibilityFilter, setEligibilityFilter] = useState<
    "all" | "eligible" | "ineligible"
  >("all");
  const [promotionStatusFilter, setPromotionStatusFilter] = useState<
    PromotionStatus | "all"
  >("all");
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotions, setPromotions] = useState<GetStudentPromotionDto[]>([]);
  const [selectedPromotion, setSelectedPromotion] =
    useState<GetStudentPromotionDto | null>(null);
  const [statusFilter, setStatusFilter] = useState<PromotionStatus | "all">(
    "all"
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [fromClassFilter, setFromClassFilter] = useState("");
  const [toClassFilter, setToClassFilter] = useState("");
  const [promotionTypeFilter, setPromotionTypeFilter] = useState<
    PromotionType | "all"
  >("all");
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const colors = useThemeColors();
  const icons = useIcons();
  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });
  const { showErrorToast } = useToaster();
  const { classes, sessions, school } = useSchoolsContext();
  const { students } = useStudentsContext();

  // Transform API data to handle null values and numeric enums, and map IDs to names
  const transformedPromotions = useMemo(() => {
    if (!promotions || !students || !classes || !sessions) {
      return [];
    }

    return promotions.map((promotion) => {
      // Find student by ID
      const student = students.find((s) => s.id === promotion.studentId);
      const studentName = student
        ? `${student.firstName} ${student.middleName} ${student.lastName}`.trim()
        : promotion.studentName || "Unknown Student";

      // Find classes by ID
      const fromClass = classes.find((c) => c.id === promotion.fromClassId);
      const toClass = classes.find((c) => c.id === promotion.toClassId);
      const fromClassName =
        fromClass?.className || promotion.fromClassName || "Unknown Class";
      const toClassName =
        toClass?.className || promotion.toClassName || "Unknown Class";

      // Find session by ID
      const session = sessions.find((s) => s.id === promotion.sessionId);
      const sessionName =
        session?.sessionName || promotion.sessionName || "Unknown Session";

      return {
        ...promotion,
        studentName,
        fromClassName,
        toClassName,
        sessionName,
        status:
          typeof promotion.status === "number"
            ? Object.values(PromotionStatus)[promotion.status] ||
              PromotionStatus.Pending
            : promotion.status,
        type:
          typeof promotion.type === "number"
            ? Object.values(PromotionType)[promotion.type] ||
              PromotionType.Manual
            : promotion.type,
      };
    });
  }, [promotions, students, classes, sessions]);

  // Get current session from school's currentSessionId
  const currentSession = useMemo(() => {
    if (!school?.currentSessionId || !sessions.length) return null;
    return (
      sessions.find((session) => session.id === school.currentSessionId) || null
    );
  }, [school?.currentSessionId, sessions]);

  // Check if student can be marked for promotion
  const canMarkForPromotion = (student: PromotionEligibilityDto) => {
    return !student.hasPendingPromotion && !student.hasApprovedPromotion;
  };

  // Filtered data
  const filteredEligibility = useMemo(() => {
    return eligibilityData.filter((item) => {
      // Search filter
      const matchesSearch = item.studentName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // Eligibility filter
      const matchesEligibility =
        eligibilityFilter === "all" ||
        (eligibilityFilter === "eligible" && item.isEligible) ||
        (eligibilityFilter === "ineligible" && !item.isEligible);

      // Promotion status filter
      const matchesPromotionStatus =
        promotionStatusFilter === "all" ||
        (promotionStatusFilter === PromotionStatus.Available &&
          canMarkForPromotion(item)) ||
        (promotionStatusFilter === PromotionStatus.Pending &&
          item.hasPendingPromotion) ||
        (promotionStatusFilter === PromotionStatus.Approved &&
          item.hasApprovedPromotion) ||
        (promotionStatusFilter === PromotionStatus.Rejected &&
          item.hasRejectedPromotion);

      // // Class filter
      // const matchesClass =
      //   selectedClass === "" || item.currentClassId === selectedClass;

      // Level filter
      const matchesLevel =
        selectedLevel === "" ||
        (() => {
          const studentClass = classes.find(
            (cls) => cls.id === item.currentClassId
          );
          return studentClass?.levelId === selectedLevel;
        })();

      return (
        matchesSearch &&
        matchesEligibility &&
        matchesPromotionStatus &&
        //matchesClass &&
        matchesLevel
      );
    });
  }, [
    eligibilityData,
    searchTerm,
    eligibilityFilter,
    promotionStatusFilter,
    selectedClass,
    selectedLevel,
    classes,
  ]);

  const filteredPromotions = useMemo((): GetStudentPromotionDto[] => {
    // Since server handles filtering, just return transformed promotions
    return transformedPromotions;
  }, [transformedPromotions]);

  // Handle bulk selection
  const handleSelectAll = () => {
    const availableStudents = filteredEligibility
      .filter((item) => canMarkForPromotion(item))
      .map((item) => item.studentId);
    setSelectedStudents(availableStudents);
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  // Handle student selection with promotion status check
  const handleStudentSelection = (studentId: string) => {
    const student = eligibilityData.find(
      (item) => item.studentId === studentId
    );

    // Don't allow selection if student already has a pending or approved promotion
    if (
      student &&
      (student.hasPendingPromotion || student.hasApprovedPromotion)
    ) {
      return;
    }

    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Load eligibility data
  const loadEligibilityData = async (classId?: string) => {
    if (!currentSession?.id) {
      showErrorToast(
        "No active session found. Please set a current session for the school."
      );
      return;
    }

    await getPromotionEligibility(
      currentSession.id,
      classId || selectedClass || undefined
    );
  };

  // Promotion Rules Management
  const getPromotionRules = useCallback(
    async (
      onSuccess?: (promotionRules: PromotionRuleDto[]) => void
    ): Promise<{
      result: ApiDataResponse<PromotionRuleDto[]>;
    }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<PromotionRuleDto[]>(
          "/promotion/rules",
          axiosClient
        ).getAll();

        if (result.succeeded) {
          handleApiSuccess("Promotion rules fetched successfully");
          onSuccess?.(result.data);
          return { result };
        } else {
          handleApiError(result, "Failed to fetch promotion rules");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to fetch promotion rules"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to fetch promotion rules",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const getPromotionRule = useCallback(
    async (
      id: string
    ): Promise<{ result: ApiDataResponse<PromotionRuleDto> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<PromotionRuleDto>(
          "/promotion/rules",
          axiosClient
        ).getById(id);

        if (result.succeeded) {
          handleApiSuccess("Promotion rule fetched successfully");
          return { result };
        } else {
          handleApiError(result, "Failed to fetch promotion rule");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to fetch promotion rule"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to fetch promotion rule",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const createPromotionRule = useCallback(
    async (
      rule: PromotionRuleDto,
      onSuccess?: (rule: PromotionRuleDto) => void
    ): Promise<{ result: ApiDataResponse<PromotionRuleDto> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<PromotionRuleDto>(
          "/promotion/rules",
          axiosClient
        ).create(rule);

        if (result.succeeded) {
          handleApiSuccess("Promotion rule created successfully");
          onSuccess?.(rule);
          return { result };
        } else {
          handleApiError(result, "Failed to create promotion rule");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to create promotion rule"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create promotion rule",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const updatePromotionRule = useCallback(
    async (
      id: string,
      rule: PromotionRuleDto,
      onSuccess?: (rule: PromotionRuleDto) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<PromotionRuleDto>(
          "/promotion/rules",
          axiosClient
        ).update(id, rule);

        if (result.succeeded) {
          handleApiSuccess("Promotion rule updated successfully");
          onSuccess?.(rule);
          return { result };
        } else {
          handleApiError(result, "Failed to update promotion rule");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to update promotion rule"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update promotion rule",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const deletePromotionRule = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<PromotionRuleDto>(
          "/promotion/rules",
          axiosClient
        ).delete(id);

        if (result.succeeded) {
          handleApiSuccess("Promotion rule deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete promotion rule");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to delete promotion rule"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete promotion rule",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  // Promotion Eligibility
  const getPromotionEligibility = useCallback(
    async (
      sessionId: string,
      classId?: string
    ): Promise<{ result: ApiDataResponse<PromotionEligibilityDto[]> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const params = new URLSearchParams({ sessionId });
        if (classId) {
          params.append("classId", classId);
        }
        const url = `/promotion/eligibility?${params.toString()}`;

        const result = await new ApiService<PromotionEligibilityDto[]>(
          url,
          axiosClient
        ).getAll();

        if (result.succeeded) {
          handleApiSuccess("Promotion eligibility fetched successfully");
          setEligibilityData(result.data);
          return { result };
        } else {
          handleApiError(result, "Failed to fetch promotion eligibility");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to fetch promotion eligibility"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to fetch promotion eligibility",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  // Promotion Execution
  const promoteStudents = useCallback(
    async (
      request: BulkPromotionRequestDto,
      onSuccess?: (result: any) => void
    ): Promise<{ result: ApiDataResponse<any> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        if (selectedStudents.length === 0) {
          showErrorToast("Please select at least one student");
          return {
            result: {
              succeeded: false,
              message: "Please select at least one student",
              statusCode: 400,
              errors: [],
              data: null as any,
            },
          };
        }

        if (!currentSession?.id) {
          showErrorToast(
            "No active session found. Please set a current session for the school."
          );
          return {
            result: {
              succeeded: false,
              message:
                "No active session found. Please set a current session for the school.",
              statusCode: 400,
              errors: [],
              data: null as any,
            },
          };
        }

        const result = await new ApiService<any>(
          "/promotion/promote",
          axiosClient
        ).create(request);

        if (result.succeeded) {
          handleApiSuccess("Students marked for promotion successfully");
          setSelectedStudents([]);
          onSuccess?.(result.data);
          loadEligibilityData();
          return { result };
        } else {
          handleApiError(result, "Failed to mark students for promotion");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to mark students for promotion"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to mark students for promotion",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const approvePromotion = useCallback(
    async (
      request: ApprovePromotionDto,
      onSuccess?: () => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<any>(
          `/promotion/approve/${request.promotionId}`,
          axiosClient
        ).create(request);

        if (result.succeeded) {
          handleApiSuccess("Promotion approved successfully");
          onSuccess?.();
          return { result };
        } else {
          handleApiError(result, "Failed to approve promotion");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to approve promotion"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to approve promotion",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const rejectPromotion = useCallback(
    async (
      request: RejectPromotionDto,
      onSuccess?: () => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<any>(
          `/promotion/reject/${request.promotionId}`,
          axiosClient
        ).create(request);

        if (result.succeeded) {
          handleApiSuccess("Promotion rejected successfully");
          onSuccess?.();
          return { result };
        } else {
          handleApiError(result, "Failed to reject promotion");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to reject promotion"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to reject promotion",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  // Session Change Promotions
  const processSessionChange = useCallback(
    async (
      request: SessionChangePromotionDto,
      onSuccess?: (result: any) => void
    ): Promise<{ result: ApiDataResponse<any> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<any>(
          `/promotion/session-change?newSessionId=${request.newSessionId}`,
          axiosClient
        ).create(request);

        if (result.succeeded) {
          handleApiSuccess("Session change processed successfully");
          onSuccess?.(result.data);
          return { result };
        } else {
          handleApiError(result, "Failed to process session change");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to process session change"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to process session change",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  // Promotion History
  const getSessionPromotions = useCallback(
    async (
      sessionId: string,
      filters?: {
        status?: string;
        fromClassId?: string;
        toClassId?: string;
        type?: string;
        searchTerm?: string;
        pageNumber?: number;
        pageSize?: number;
      }
    ): Promise<{ result: ApiDataResponse<PromotionPagedData> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        if (!currentSession?.id) {
          showErrorToast(
            "No active session found. Please set a current session for the school."
          );
          setPromotions([]);
          return {
            result: {
              succeeded: false,
              message:
                "No active session found. Please set a current session for the school.",
              statusCode: 400,
              errors: [],
              data: {
                data: [],
                pageNumber: 1,
                pageSize: 0,
                totalPages: 0,
                totalRecords: 0,
              },
            },
          };
        }

        // Build query parameters
        const params = new URLSearchParams();
        if (filters?.status && filters.status !== "all") {
          params.append("promotionStatus", filters.status);
        }
        if (filters?.fromClassId) {
          params.append("fromClassId", filters.fromClassId);
        }
        if (filters?.toClassId) {
          params.append("toClassId", filters.toClassId);
        }
        if (filters?.type && filters.type !== "all") {
          params.append("promotionType", filters.type);
        }
        if (filters?.searchTerm) {
          params.append("searchTerm", filters.searchTerm);
        }
        if (filters?.pageNumber) {
          params.append("pageNumber", filters.pageNumber.toString());
        }
        if (filters?.pageSize) {
          params.append("pageSize", filters.pageSize.toString());
        }

        const url = `/promotion/history/session/${sessionId}/filtered?${params.toString()}`;
        const result = await new ApiService<PromotionPagedData>(
          url,
          axiosClient
        ).getAll();

        if (result.succeeded) {
          setPromotions(result.data.data);
          setCurrentPage(result.data.pageNumber);
          setPageSize(result.data.pageSize);
          setTotalPages(result.data.totalPages);
          setTotalRecords(result.data.totalRecords);
          setHasAttemptedFetch(true);
          handleApiSuccess("Promotion history fetched successfully");
          return { result };
        } else {
          handleApiError(result, "Failed to fetch promotion history");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to fetch promotion history"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to fetch promotion history",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: {
              data: [],
              pageNumber: 1,
              pageSize: 0,
              totalPages: 0,
              totalRecords: 0,
            },
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors, currentSession]
  );

  const getPromotionsByStatus = useCallback(
    async (
      status: string
    ): Promise<{ result: ApiDataResponse<GetStudentPromotionDto[]> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<GetStudentPromotionDto[]>(
          `/promotion/status/${status}`,
          axiosClient
        ).getAll();

        if (result.succeeded) {
          handleApiSuccess("Promotions by status fetched successfully");
          return { result };
        } else {
          handleApiError(result, "Failed to fetch promotions by status");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to fetch promotions by status"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to fetch promotions by status",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  // Get promotion status info for display
  const getPromotionStatusInfo = (student: PromotionEligibilityDto) => {
    if (student.hasPendingPromotion) {
      return {
        color: "yellow",
        icon: icons.utility.clock,
        label: "Pending Approval",
        bg: colors.status.warningBg,
        borderColor: colors.status.warningBorder,
      };
    }
    if (student.hasApprovedPromotion) {
      return {
        color: "blue",
        icon: icons.utility.check,
        label: "Approved",
        bg: colors.status.infoBg,
        borderColor: colors.status.infoBorder,
      };
    }
    if (student.hasRejectedPromotion) {
      return {
        color: "red",
        icon: icons.utility.cancel,
        label: "Rejected",
        bg: colors.status.errorBg,
        borderColor: colors.status.errorBorder,
      };
    }
    return null;
  };

  // Get status info
  const getStatusInfo = (status: PromotionStatus) => {
    switch (status) {
      case PromotionStatus.Pending:
        return { color: "yellow", icon: icons.utility.clock, label: "Pending" };
      case PromotionStatus.Approved:
        return { color: "blue", icon: icons.utility.check, label: "Approved" };
      case PromotionStatus.Rejected:
        return { color: "red", icon: icons.utility.cancel, label: "Rejected" };
      case PromotionStatus.Promoted:
        return {
          color: "green",
          icon: icons.main.promotion,
          label: "Promoted",
        };
      case PromotionStatus.Failed:
        return { color: "red", icon: icons.utility.error, label: "Failed" };
      default:
        return { color: "gray", icon: icons.utility.clock, label: "Unknown" };
    }
  };

  // Get type info
  const getTypeInfo = (type: PromotionType) => {
    switch (type) {
      case PromotionType.Automatic:
        return { color: "green", label: "Automatic" };
      case PromotionType.Manual:
        return { color: "blue", label: "Manual" };
      case PromotionType.SessionChange:
        return { color: "purple", label: "Session Change" };
      default:
        return { color: "gray", label: "Unknown" };
    }
  };

  const getEligibilityStatusColor = (isEligible: boolean) => {
    return isEligible ? "green" : "red";
  };

  const getEligibilityIcon = (isEligible: boolean) => {
    return isEligible ? icons.utility.check : icons.utility.clock;
  };

  const getEligibilityText = (isEligible: boolean) => {
    return isEligible ? "Eligible" : "Not Eligible";
  };

  return {
    // Promotion Rules
    getPromotionRules,
    getPromotionRule,
    createPromotionRule,
    updatePromotionRule,
    deletePromotionRule,

    // Promotion Eligibility
    getPromotionEligibility,
    eligibilityData,
    setEligibilityData,
    getPromotionStatusInfo,
    selectedStudents,
    setSelectedStudents,
    getEligibilityStatusColor,
    getEligibilityIcon,
    getEligibilityText,
    selectedClass,
    setSelectedClass,
    selectedLevel,
    setSelectedLevel,
    searchTerm,
    setSearchTerm,
    eligibilityFilter,
    setEligibilityFilter,
    promotionStatusFilter,
    setPromotionStatusFilter,
    promotionLoading,
    setPromotionLoading,
    // Promotion Execution
    promoteStudents,
    approvePromotion,
    rejectPromotion,

    // Session Change
    processSessionChange,

    // Promotion History
    getSessionPromotions,
    getPromotionsByStatus,
    promotions,
    setPromotions,
    selectedPromotion,
    setSelectedPromotion,
    statusFilter,
    setStatusFilter,
    rejectionReason,
    setRejectionReason,
    fromClassFilter,
    setFromClassFilter,
    toClassFilter,
    setToClassFilter,
    promotionTypeFilter,
    setPromotionTypeFilter,
    filteredPromotions,
    getTypeInfo,
    getStatusInfo,
    transformedPromotions,
    hasAttemptedFetch,
    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    // State
    isLoading,
    errors,
    clearErrors,
    currentSession,
    loadEligibilityData,
    filteredEligibility,
    handleSelectAll,
    handleDeselectAll,
    canMarkForPromotion,
    handleStudentSelection,
  };
};
 